import { InjectionToken } from '@angular/core'

/**
 * Runtime maintenance switch, read from /config.json before the application
 * bootstraps. The container entrypoint fills these keys from the MAINTENANCE,
 * MAINTENANCE_UNTIL and MAINTENANCE_CONTACT environment variables. Absent or
 * empty means the app runs normally.
 */
export interface MaintenanceConfig {
  enabled: boolean
  /** Free text shown after "We will be back on", e.g. "Friday 23 October, 17:00 CEST". */
  until: string | null
  /** Contact email shown on the page. */
  contact: string
}

export const DEFAULT_MAINTENANCE_CONTACT = 'thomas@yoshu.ai'

export const MAINTENANCE_CONFIG = new InjectionToken<MaintenanceConfig>('MAINTENANCE_CONFIG')

/** Accepts a JSON boolean or the string "true" (any case). Anything else is false. */
export function parseMaintenanceFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true'
  return false
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

export function parseMaintenanceConfig(raw: unknown): MaintenanceConfig {
  const config = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return {
    enabled: parseMaintenanceFlag(config['maintenance']),
    until: nonEmptyString(config['maintenanceUntil']),
    contact: nonEmptyString(config['maintenanceContact']) ?? DEFAULT_MAINTENANCE_CONTACT
  }
}

/**
 * Fetches /config.json (same origin, never the API). Any failure means
 * "not in maintenance" so a missing or broken file never blocks the app.
 */
export async function loadMaintenanceConfig(
  fetchFn: typeof fetch = (input, init) => fetch(input, init)
): Promise<MaintenanceConfig> {
  try {
    const response = await fetchFn('/config.json', { cache: 'no-store' })
    if (!response.ok) return parseMaintenanceConfig(null)
    return parseMaintenanceConfig(await response.json())
  } catch {
    return parseMaintenanceConfig(null)
  }
}
